import React, { useEffect, useState, useCallback } from "react";
import { Table, Spinner, Button, Form, Row, Col, Badge, Alert as BsAlert } from "react-bootstrap";
import SafetyAttrezzatureService from "../../../services/safety/SafetyAttrezzatureService";

const EMPTY_ATTR = {
    id_tipo_attr: "",
    categoria: "",
    denominazione: "",
    matricola: "",
    costruttore: "",
    modello: "",
    anno_costruzione: "",
    ubicazione: "",
    data_messa_servizio: "",
    periodicita_verifica_mesi: "",
    data_ultima_verifica: "",
    organismo_verificatore: "",
    note: "",
};

const EMPTY_VER = {
    tipo_verifica: "",
    data_verifica: "",
    esito: "POSITIVO",
    organismo_verificatore: "",
    numero_verbale: "",
    periodicita_mesi: "",
    data_prossima_verifica: "",
    prescrizioni: "",
    note: "",
};

const CATEGORIE = [
    { value: "ATTREZZATURA", label: "Attrezzatura (art. 71)" },
    { value: "IMPIANTO",     label: "Impianto (DPR 462)" },
    { value: "ANTINCENDIO",  label: "Presidio antincendio" },
];

const ESITI = [
    { value: "POSITIVO",         label: "Positivo" },
    { value: "CON_PRESCRIZIONI", label: "Con prescrizioni" },
    { value: "NEGATIVO",         label: "Negativo" },
    { value: "NON_ESEGUITA",     label: "Non eseguita" },
];

function fmtDate(d) {
    if (!d) return "—";
    const s = String(d).substring(0, 10);
    const p = s.split("-");
    if (p.length !== 3) return s;
    return `${p[2]}/${p[1]}/${p[0]}`;
}

function statoBadge(stato, giorni) {
    const map = {
        SCADUTA:        { bg: "danger",  text: "light", label: "Scaduta" },
        IN_SCADENZA:    { bg: "warning", text: "dark",  label: "In scadenza" },
        VALIDA:         { bg: "success", text: "light", label: "Valida" },
        DA_PIANIFICARE: { bg: "light",   text: "dark",  label: "Da pianificare" },
    };
    const m = map[stato] || map.DA_PIANIFICARE;
    return (
        <span>
            <Badge bg={m.bg} text={m.text} className={m.bg === "light" ? "border" : undefined}>{m.label}</Badge>
            {typeof giorni === "number" && (stato === "SCADUTA" || stato === "IN_SCADENZA") && (
                <span className="text-muted small ms-1">
                    ({giorni < 0 ? `${Math.abs(giorni)} gg fa` : `tra ${giorni} gg`})
                </span>
            )}
        </span>
    );
}

function esitoBadge(esito) {
    if (!esito) return <span className="text-muted">—</span>;
    const tone = esito === "POSITIVO" ? "success"
        : esito === "CON_PRESCRIZIONI" ? "warning"
        : esito === "NEGATIVO" ? "danger" : "secondary";
    const label = ESITI.find(e => e.value === esito)?.label || esito;
    return <Badge bg={tone} text={tone === "warning" ? "dark" : "light"}>{label}</Badge>;
}

/**
 * AttrezzaturePanel
 * Gestione attrezzature/impianti soggetti a verifica periodica di un'azienda,
 * con storico verifiche per ciascuna attrezzatura.
 *
 * Props: idAzienda (number)
 */
export default function AttrezzaturePanel({ idAzienda, settore }) {
    const [tipi, setTipi] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [msg, setMsg] = useState(null);

    const [show, setShow] = useState(false);
    const [form, setForm] = useState(EMPTY_ATTR);
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);

    // Verifiche: id attrezzatura espansa + relativo stato
    const [openId, setOpenId] = useState(null);
    const [verifiche, setVerifiche] = useState([]);
    const [verLoading, setVerLoading] = useState(false);
    const [verShow, setVerShow] = useState(false);
    const [verForm, setVerForm] = useState(EMPTY_VER);
    const [verFile, setVerFile] = useState(null);
    const [verSaving, setVerSaving] = useState(false);

    const loadTipi = useCallback(async () => {
        try {
            const resp = await SafetyAttrezzatureService.getTipi("", settore);
            const data = resp && typeof resp.json === "function" ? await resp.json() : resp;
            setTipi(Array.isArray(data) ? data : []);
        } catch {
            setTipi([]);
        }
    }, [settore]);

    const loadItems = useCallback(async () => {
        if (!idAzienda) { setItems([]); return; }
        setLoading(true);
        setError(null);
        try {
            const resp = await SafetyAttrezzatureService.byAzienda(idAzienda);
            const data = resp && typeof resp.json === "function" ? await resp.json() : resp;
            setItems(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err?.message || "Errore nel caricamento delle attrezzature.");
        } finally {
            setLoading(false);
        }
    }, [idAzienda]);

    useEffect(() => { loadTipi(); }, [loadTipi]);
    useEffect(() => { loadItems(); }, [loadItems]);

    const openForm = () => {
        setForm(EMPTY_ATTR);
        setFile(null);
        setError(null);
        setMsg(null);
        setShow(true);
    };

    const onTipoChange = (val) => {
        const t = tipi.find(x => Number(x.id_tipo_attr) === Number(val));
        setForm(f => ({
            ...f,
            id_tipo_attr: val,
            categoria: t ? t.categoria : f.categoria,
            denominazione: f.denominazione || (t ? t.denominazione : ""),
            periodicita_verifica_mesi: (f.periodicita_verifica_mesi === "" && t && t.periodicita_mesi != null)
                ? String(t.periodicita_mesi)
                : f.periodicita_verifica_mesi,
        }));
    };

    const submit = async () => {
        if (!idAzienda) return;
        if (!form.id_tipo_attr && !form.denominazione) {
            setError("Seleziona un tipo o indica una denominazione.");
            return;
        }
        setSaving(true);
        setError(null);
        setMsg(null);
        try {
            const fd = new FormData();
            fd.append("id_azienda", idAzienda);
            Object.entries(form).forEach(([k, v]) => {
                if (v !== "" && v != null) fd.append(k, v);
            });
            if (file) fd.append("file", file);
            await SafetyAttrezzatureService.create(fd);
            setMsg("Attrezzatura aggiunta.");
            setShow(false);
            setForm(EMPTY_ATTR);
            setFile(null);
            await loadItems();
        } catch (err) {
            setError(err?.message || "Errore nel salvataggio.");
        } finally {
            setSaving(false);
        }
    };

    const onDelete = async (a) => {
        if (!window.confirm(`Eliminare l'attrezzatura "${a.denominazione}" e il suo storico verifiche?`)) return;
        setError(null);
        try {
            await SafetyAttrezzatureService.deleteAttrezzatura(a.id_attrezzatura);
            if (openId === a.id_attrezzatura) setOpenId(null);
            await loadItems();
        } catch (err) {
            setError(err?.message || "Errore nell'eliminazione.");
        }
    };

    const onDownload = async (a) => {
        setError(null);
        try {
            const fn = `${(a.denominazione || "libretto").replace(/[^\w\-]+/g, "_")}.pdf`;
            await SafetyAttrezzatureService.download(a.id_attrezzatura, fn);
        } catch (err) {
            setError(err?.message || "File non disponibile.");
        }
    };

    // ---- Verifiche ----
    const loadVerifiche = useCallback(async (idAttr) => {
        setVerLoading(true);
        try {
            const resp = await SafetyAttrezzatureService.verifiche(idAttr);
            const data = resp && typeof resp.json === "function" ? await resp.json() : resp;
            setVerifiche(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err?.message || "Errore nel caricamento delle verifiche.");
            setVerifiche([]);
        } finally {
            setVerLoading(false);
        }
    }, []);

    const toggleVerifiche = (a) => {
        if (openId === a.id_attrezzatura) {
            setOpenId(null);
            setVerShow(false);
            return;
        }
        setOpenId(a.id_attrezzatura);
        setVerShow(false);
        setVerForm({ ...EMPTY_VER, periodicita_mesi: a.periodicita_verifica_mesi || "" });
        loadVerifiche(a.id_attrezzatura);
    };

    const openVerForm = (a) => {
        setVerForm({ ...EMPTY_VER, periodicita_mesi: a.periodicita_verifica_mesi || "" });
        setVerFile(null);
        setVerShow(true);
    };

    const submitVerifica = async (a) => {
        if (!verForm.data_verifica) {
            setError("Indica la data della verifica.");
            return;
        }
        setVerSaving(true);
        setError(null);
        try {
            const fd = new FormData();
            fd.append("id_attrezzatura", a.id_attrezzatura);
            Object.entries(verForm).forEach(([k, v]) => {
                if (v !== "" && v != null) fd.append(k, v);
            });
            if (verFile) fd.append("file", verFile);
            await SafetyAttrezzatureService.createVerifica(fd);
            setVerShow(false);
            setVerForm(EMPTY_VER);
            setVerFile(null);
            await loadVerifiche(a.id_attrezzatura);
            await loadItems();
        } catch (err) {
            setError(err?.message || "Errore nel salvataggio della verifica.");
        } finally {
            setVerSaving(false);
        }
    };

    const onDeleteVerifica = async (v, a) => {
        if (!window.confirm("Eliminare questa verifica?")) return;
        setError(null);
        try {
            await SafetyAttrezzatureService.deleteVerifica(v.id_verifica);
            await loadVerifiche(a.id_attrezzatura);
            await loadItems();
        } catch (err) {
            setError(err?.message || "Errore nell'eliminazione.");
        }
    };

    const onDownloadVerbale = async (v) => {
        setError(null);
        try {
            const fn = `verbale_${v.id_verifica}.pdf`;
            await SafetyAttrezzatureService.downloadVerifica(v.id_verifica, fn);
        } catch (err) {
            setError(err?.message || "Verbale non disponibile.");
        }
    };

    const tipoSel = form.id_tipo_attr ? tipi.find(t => Number(t.id_tipo_attr) === Number(form.id_tipo_attr)) : null;

    if (!idAzienda) {
        return (
            <BsAlert variant="info" className="py-2 mb-0">
                <i className="bi bi-info-circle me-1" />
                Salva prima i dati dell'azienda per gestire le attrezzature.
            </BsAlert>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">
                    <i className="bi bi-tools me-2 text-primary" />
                    Attrezzature e verifiche periodiche
                </h6>
                <Button size="sm" variant="primary" onClick={openForm} disabled={show}>
                    <i className="bi bi-plus-lg me-1" />
                    Aggiungi attrezzatura
                </Button>
            </div>

            <div className="text-muted small mb-2">
                <i className="bi bi-info-circle me-1" />
                Registra macchine/attrezzature soggette a verifica (art. 71, All. VII), impianti di
                terra/scariche atmosferiche (DPR 462/01) e presìdi antincendio. Per ognuna, usa
                <strong> "Verifiche" </strong> per registrare i controlli periodici: lo stato e la
                prossima scadenza si aggiornano in automatico.
            </div>

            {show && (
                <div className="border rounded p-3 mb-3" style={{ backgroundColor: "#f8f9fa" }}>
                    <div className="fw-semibold mb-2">
                        <i className="bi bi-plus-square me-1 text-primary" />
                        Nuova attrezzatura / impianto
                    </div>
                    <Row className="g-2">
                        <Col xs={12} md={6}>
                            <Form.Label className="small mb-0">Tipo (dal catalogo)</Form.Label>
                            <Form.Select size="sm" value={form.id_tipo_attr} onChange={e => onTipoChange(e.target.value)}>
                                <option value="">— Nessuno / altro —</option>
                                {tipi.map(t => (
                                    <option key={t.id_tipo_attr} value={t.id_tipo_attr}>
                                        [{t.categoria}] {t.denominazione}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Text className="text-muted">
                                Imposta categoria e periodicità di verifica suggerita.
                            </Form.Text>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Label className="small mb-0">Denominazione</Form.Label>
                            <Form.Control
                                size="sm"
                                placeholder={tipoSel ? tipoSel.denominazione : "Es. Carrello elevatore reparto A"}
                                value={form.denominazione}
                                onChange={e => setForm(f => ({ ...f, denominazione: e.target.value }))}
                            />
                        </Col>

                        {tipoSel && tipoSel.riferimento_normativo && (
                            <Col xs={12}>
                                <BsAlert variant="info" className="py-2 mb-1 small">
                                    <i className="bi bi-lightbulb me-1" />
                                    {tipoSel.denominazione} — <span className="text-muted">{tipoSel.riferimento_normativo}</span>
                                    {Number(tipoSel.verifica_terzo) === 1 && (
                                        <span> · Verifica da effettuarsi tramite organismo abilitato/ASL-ARPA.</span>
                                    )}
                                </BsAlert>
                            </Col>
                        )}

                        <Col xs={6} md={3}>
                            <Form.Label className="small mb-0">Categoria</Form.Label>
                            <Form.Select size="sm" value={form.categoria} onChange={e => setForm(f => ({ ...f, categoria: e.target.value }))}>
                                <option value="">—</option>
                                {CATEGORIE.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </Form.Select>
                        </Col>
                        <Col xs={6} md={3}>
                            <Form.Label className="small mb-0">Matricola / n. fabbrica</Form.Label>
                            <Form.Control size="sm" value={form.matricola} onChange={e => setForm(f => ({ ...f, matricola: e.target.value }))} />
                        </Col>
                        <Col xs={6} md={3}>
                            <Form.Label className="small mb-0">Costruttore</Form.Label>
                            <Form.Control size="sm" value={form.costruttore} onChange={e => setForm(f => ({ ...f, costruttore: e.target.value }))} />
                        </Col>
                        <Col xs={6} md={3}>
                            <Form.Label className="small mb-0">Modello</Form.Label>
                            <Form.Control size="sm" value={form.modello} onChange={e => setForm(f => ({ ...f, modello: e.target.value }))} />
                        </Col>

                        <Col xs={6} md={3}>
                            <Form.Label className="small mb-0">Anno costruzione</Form.Label>
                            <Form.Control size="sm" type="number" min="1900" max="2100" value={form.anno_costruzione} onChange={e => setForm(f => ({ ...f, anno_costruzione: e.target.value }))} />
                        </Col>
                        <Col xs={6} md={3}>
                            <Form.Label className="small mb-0">Ubicazione</Form.Label>
                            <Form.Control size="sm" placeholder="Sede / reparto" value={form.ubicazione} onChange={e => setForm(f => ({ ...f, ubicazione: e.target.value }))} />
                        </Col>
                        <Col xs={6} md={3}>
                            <Form.Label className="small mb-0">Periodicità verifica (mesi)</Form.Label>
                            <Form.Control size="sm" type="number" min="0" placeholder="es. 12" value={form.periodicita_verifica_mesi} onChange={e => setForm(f => ({ ...f, periodicita_verifica_mesi: e.target.value }))} />
                            <Form.Text className="text-muted">Per il calcolo della prossima verifica.</Form.Text>
                        </Col>
                        <Col xs={6} md={3}>
                            <Form.Label className="small mb-0">Data messa in servizio</Form.Label>
                            <Form.Control size="sm" type="date" value={form.data_messa_servizio} onChange={e => setForm(f => ({ ...f, data_messa_servizio: e.target.value }))} />
                        </Col>

                        <Col xs={6} md={3}>
                            <Form.Label className="small mb-0">Data ultima verifica</Form.Label>
                            <Form.Control size="sm" type="date" value={form.data_ultima_verifica} onChange={e => setForm(f => ({ ...f, data_ultima_verifica: e.target.value }))} />
                            <Form.Text className="text-muted">Opzionale: imposta subito la prossima scadenza.</Form.Text>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Label className="small mb-0">Organismo / verificatore</Form.Label>
                            <Form.Control size="sm" placeholder="Es. INAIL, ASL, organismo abilitato, manutentore" value={form.organismo_verificatore} onChange={e => setForm(f => ({ ...f, organismo_verificatore: e.target.value }))} />
                        </Col>
                        <Col xs={12} md={3}>
                            <Form.Label className="small mb-0">Libretto / manuale (file)</Form.Label>
                            <Form.Control size="sm" type="file" accept="application/pdf,image/*" onChange={e => setFile(e.target.files?.[0] || null)} />
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="small mb-0">Note</Form.Label>
                            <Form.Control as="textarea" rows={2} size="sm" value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
                        </Col>
                    </Row>
                    <div className="d-flex justify-content-end gap-2 mt-2">
                        <Button size="sm" variant="secondary" onClick={() => setShow(false)} disabled={saving}>Annulla</Button>
                        <Button size="sm" variant="primary" onClick={submit} disabled={saving}>
                            {saving ? <Spinner size="sm" animation="border" className="me-1" /> : <i className="bi bi-check2 me-1" />}
                            Salva attrezzatura
                        </Button>
                    </div>
                </div>
            )}

            {error && <BsAlert variant="danger" className="py-2">{error}</BsAlert>}
            {msg && <BsAlert variant="success" className="py-2">{msg}</BsAlert>}

            {loading ? (
                <div className="text-muted small"><Spinner size="sm" animation="border" className="me-2" />Caricamento attrezzature…</div>
            ) : items.length === 0 ? (
                <div className="text-muted small">
                    Nessuna attrezzatura registrata. Usa <strong>"Aggiungi attrezzatura"</strong> per
                    censire macchine, impianti e presìdi soggetti a verifica periodica.
                </div>
            ) : (
                <Table size="sm" bordered hover responsive className="mb-0">
                    <thead className="table-light">
                        <tr>
                            <th>Attrezzatura</th>
                            <th>Categoria</th>
                            <th>Ultima verifica</th>
                            <th>Prossima</th>
                            <th>Stato</th>
                            <th className="text-end">Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(a => (
                            <React.Fragment key={a.id_attrezzatura}>
                                <tr>
                                    <td>
                                        <div className="fw-semibold">{a.denominazione}</div>
                                        <div className="text-muted small">
                                            {a.matricola ? `Matr. ${a.matricola}` : ""}
                                            {a.ubicazione ? `${a.matricola ? " · " : ""}${a.ubicazione}` : ""}
                                        </div>
                                    </td>
                                    <td><Badge bg="secondary">{a.categoria}</Badge></td>
                                    <td>{fmtDate(a.data_ultima_verifica)} {esitoBadge(a.esito_ultima_verifica)}</td>
                                    <td>{fmtDate(a.data_prossima_verifica)}</td>
                                    <td>{statoBadge(a.stato, a.giorni_alla_scadenza)}</td>
                                    <td className="text-end" style={{ whiteSpace: "nowrap" }}>
                                        <Button size="sm" variant={openId === a.id_attrezzatura ? "primary" : "outline-primary"} className="me-1" onClick={() => toggleVerifiche(a)} title="Storico verifiche">
                                            <i className="bi bi-clipboard-check me-1" />
                                            Verifiche{Number(a.num_verifiche) > 0 ? ` (${a.num_verifiche})` : ""}
                                        </Button>
                                        {a.path_file && (
                                            <Button size="sm" variant="danger" className="me-1" onClick={() => onDownload(a)} title="Scarica libretto">
                                                <i className="bi bi-file-earmark-pdf" />
                                            </Button>
                                        )}
                                        <Button size="sm" variant="outline-danger" onClick={() => onDelete(a)} title="Elimina">
                                            <i className="bi bi-trash" />
                                        </Button>
                                    </td>
                                </tr>
                                {openId === a.id_attrezzatura && (
                                    <tr>
                                        <td colSpan={6} style={{ backgroundColor: "#fbfbfc" }}>
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <span className="fw-semibold small">
                                                    <i className="bi bi-clock-history me-1 text-primary" />
                                                    Storico verifiche — {a.denominazione}
                                                </span>
                                                <Button size="sm" variant="success" onClick={() => openVerForm(a)} disabled={verShow}>
                                                    <i className="bi bi-plus-lg me-1" />
                                                    Registra verifica
                                                </Button>
                                            </div>

                                            {verShow && (
                                                <div className="border rounded p-2 mb-2 bg-white">
                                                    <Row className="g-2">
                                                        <Col xs={6} md={3}>
                                                            <Form.Label className="small mb-0">Data verifica *</Form.Label>
                                                            <Form.Control size="sm" type="date" value={verForm.data_verifica} onChange={e => setVerForm(v => ({ ...v, data_verifica: e.target.value }))} />
                                                        </Col>
                                                        <Col xs={6} md={3}>
                                                            <Form.Label className="small mb-0">Esito</Form.Label>
                                                            <Form.Select size="sm" value={verForm.esito} onChange={e => setVerForm(v => ({ ...v, esito: e.target.value }))}>
                                                                {ESITI.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                                            </Form.Select>
                                                        </Col>
                                                        <Col xs={6} md={3}>
                                                            <Form.Label className="small mb-0">Periodicità (mesi)</Form.Label>
                                                            <Form.Control size="sm" type="number" min="0" value={verForm.periodicita_mesi} onChange={e => setVerForm(v => ({ ...v, periodicita_mesi: e.target.value }))} />
                                                        </Col>
                                                        <Col xs={6} md={3}>
                                                            <Form.Label className="small mb-0">Prossima verifica</Form.Label>
                                                            <Form.Control size="sm" type="date" value={verForm.data_prossima_verifica} onChange={e => setVerForm(v => ({ ...v, data_prossima_verifica: e.target.value }))} />
                                                            <Form.Text className="text-muted">Se vuota: data + periodicità.</Form.Text>
                                                        </Col>
                                                        <Col xs={12} md={4}>
                                                            <Form.Label className="small mb-0">Tipo verifica</Form.Label>
                                                            <Form.Control size="sm" placeholder="Es. periodica, prima verifica, straordinaria" value={verForm.tipo_verifica} onChange={e => setVerForm(v => ({ ...v, tipo_verifica: e.target.value }))} />
                                                        </Col>
                                                        <Col xs={12} md={4}>
                                                            <Form.Label className="small mb-0">Organismo / verificatore</Form.Label>
                                                            <Form.Control size="sm" value={verForm.organismo_verificatore} onChange={e => setVerForm(v => ({ ...v, organismo_verificatore: e.target.value }))} />
                                                        </Col>
                                                        <Col xs={12} md={4}>
                                                            <Form.Label className="small mb-0">N. verbale</Form.Label>
                                                            <Form.Control size="sm" value={verForm.numero_verbale} onChange={e => setVerForm(v => ({ ...v, numero_verbale: e.target.value }))} />
                                                        </Col>
                                                        <Col xs={12} md={8}>
                                                            <Form.Label className="small mb-0">Prescrizioni (se presenti)</Form.Label>
                                                            <Form.Control size="sm" value={verForm.prescrizioni} onChange={e => setVerForm(v => ({ ...v, prescrizioni: e.target.value }))} />
                                                        </Col>
                                                        <Col xs={12} md={4}>
                                                            <Form.Label className="small mb-0">Verbale (file)</Form.Label>
                                                            <Form.Control size="sm" type="file" accept="application/pdf,image/*" onChange={e => setVerFile(e.target.files?.[0] || null)} />
                                                        </Col>
                                                    </Row>
                                                    <div className="d-flex justify-content-end gap-2 mt-2">
                                                        <Button size="sm" variant="secondary" onClick={() => setVerShow(false)} disabled={verSaving}>Annulla</Button>
                                                        <Button size="sm" variant="success" onClick={() => submitVerifica(a)} disabled={verSaving}>
                                                            {verSaving ? <Spinner size="sm" animation="border" className="me-1" /> : <i className="bi bi-check2 me-1" />}
                                                            Salva verifica
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}

                                            {verLoading ? (
                                                <div className="text-muted small"><Spinner size="sm" animation="border" className="me-2" />Caricamento…</div>
                                            ) : verifiche.length === 0 ? (
                                                <div className="text-muted small">Nessuna verifica registrata.</div>
                                            ) : (
                                                <Table size="sm" borderless className="mb-0 align-middle">
                                                    <thead>
                                                        <tr className="text-muted small">
                                                            <th>Data</th>
                                                            <th>Esito</th>
                                                            <th>Tipo</th>
                                                            <th>Organismo</th>
                                                            <th>N. verbale</th>
                                                            <th>Prossima</th>
                                                            <th className="text-end">Azioni</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {verifiche.map(v => (
                                                            <tr key={v.id_verifica}>
                                                                <td>{fmtDate(v.data_verifica)}</td>
                                                                <td>{esitoBadge(v.esito)}</td>
                                                                <td className="small">{v.tipo_verifica || "—"}</td>
                                                                <td className="small">{v.organismo_verificatore || "—"}</td>
                                                                <td className="small">{v.numero_verbale || "—"}</td>
                                                                <td>{fmtDate(v.data_prossima_verifica)}</td>
                                                                <td className="text-end" style={{ whiteSpace: "nowrap" }}>
                                                                    {v.path_file && (
                                                                        <Button size="sm" variant="outline-danger" className="me-1" onClick={() => onDownloadVerbale(v)} title="Scarica verbale">
                                                                            <i className="bi bi-file-earmark-pdf" />
                                                                        </Button>
                                                                    )}
                                                                    <Button size="sm" variant="outline-secondary" onClick={() => onDeleteVerifica(v, a)} title="Elimina verifica">
                                                                        <i className="bi bi-trash" />
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            )}
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </Table>
            )}
        </div>
    );
}
